import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import { fmtDateTime, fmtAgo, errMsg, cachedGet } from './shared';

// "Why was / wasn't this loan bid?" - backend utils/activity/bids.js.

const VERDICT_VARIANT = {
    placed: 'ex-status-success',
    undetermined: 'ex-status-info',
    failed: 'ex-status-danger',
    skipped: 'ex-status-warning',
    not_received: 'ex-status-danger',
    not_scored: 'ex-status-warning',
    waiting: 'ex-status-info',
    unknown: '',
};
const OUTCOME = {
    placed: { label: 'Bid placed', variant: 'ex-status-success' },
    undetermined: { label: 'Undetermined', variant: 'ex-status-info' },
    failed: { label: 'Bid failed', variant: 'ex-status-danger' },
    skipped: { label: 'Not bid', variant: 'ex-status-warning' },
    not_applicable: { label: 'Not applicable', variant: '' },
    no_record: { label: 'No decision', variant: '' },
};
const LOAN_TYPE = { SME: 'SME', CONSUMER: 'Consumer', INVOICE_FINANCING: 'Invoice financing' };

// Every allocation for the platform, by name (LoanSweeper), with its result.
const AllocationTable = ({ rows }) => (
    <DataTable value={rows} dataKey="key" size="small" responsiveLayout="scroll"
        rowClassName={(r) => (r.result === 'not_applicable' ? 'act-row-muted' : '')}>
        <Column header="Allocation" body={(r) => (
            <span title={r.key ? `Allocation ID ${r.key}` : ''}>
                <strong>{r.name}</strong>
                {(r.loan_type || r.active === false) && (
                    <small className="text-color-secondary">
                        {' · '}{LOAN_TYPE[String(r.loan_type).toUpperCase()] || r.loan_type || ''}{r.active === false ? ' · off' : ''}
                    </small>
                )}
            </span>
        )} />
        <Column header="Result" body={(r) => <span className={`ex-status ${OUTCOME[r.result]?.variant || ''}`.trim()}>{OUTCOME[r.result]?.label || r.result}</span>} />
        <Column header="Amount" body={(r) => (r.amount != null ? fmtNum(r.amount) : '—')} />
        <Column header="Why" body={(r) => <span title={r.raw || ''}>{r.reason || '—'}</span>} />
    </DataTable>
);

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const fmtPct = (r) => (r == null ? '—' : `${(Number(r) * 100).toFixed(2).replace(/\.00$/, '')}%`);

const Step = ({ done, title, children }) => (
    <li className={`act-chain-step ${done ? 'is-done' : 'is-missing'}`}>
        <i className={`pi ${done ? 'pi-check-circle' : 'pi-times-circle'}`} />
        <div><strong>{title}</strong><div className="text-color-secondary">{children}</div></div>
    </li>
);

const LoanResult = ({ result }) => {
    const { verdict, received, score, decisions, note, source_error, allocations, allocations_unavailable, loan_type } = result;
    return (
        <div className="act-loan">
            <div className={`act-explain act-verdict-${verdict.state}`}>
                <div className="act-explain-head">
                    <span className={`ex-status ${VERDICT_VARIANT[verdict.state] || ''}`}>{verdict.headline}</span>
                    <span className="text-color-secondary">Loan {result.loan_id}</span>
                </div>
                {verdict.detail && <div>{verdict.detail}</div>}
                {verdict.more_reasons?.map((r) => <div key={r}>{r}</div>)}
            </div>

            <ol className="act-chain">
                <Step done={Boolean(received)} title="Received">
                    {received
                        ? <>{fmtDateTime(received.at)}{received.amount != null && ` · ${fmtNum(received.amount)} ${received.currency || ''}`}
                            {received.rate != null && ` · rate ${fmtPct(received.rate)}`}{received.term != null && ` · term ${received.term}`}</>
                        : 'No record of this loan.'}
                </Step>
                <Step done={Boolean(score?.grade)} title="Scored">
                    {score?.grade ? <>Grade <strong>{score.grade}</strong>{score.pd != null && ` · PD ${fmtPct(score.pd)}`}</> : 'No score.'}
                </Step>
                <Step done={decisions.length > 0} title="Bidding decision">
                    {decisions.length ? `${decisions.length} allocation${decisions.length === 1 ? '' : 's'} evaluated this loan.` : (note || 'No decision recorded.')}
                    {loan_type && <div>Loan type: {LOAN_TYPE[loan_type] || loan_type}</div>}
                </Step>
            </ol>

            {allocations && allocations.length > 0 && <AllocationTable rows={allocations} />}
            {allocations_unavailable && decisions.length > 0 && (
                <DataTable value={decisions} size="small" responsiveLayout="scroll">
                    <Column header="Allocation" body={(d) => d.allocation || '—'} />
                    <Column header="Result" body={(d) => <span className={`ex-status ${OUTCOME[d.outcome]?.variant || ''}`}>{OUTCOME[d.outcome]?.label || d.outcome}</span>} />
                    <Column header="Amount" body={(d) => (d.amount != null ? fmtNum(d.amount) : '—')} />
                    <Column header="Why" body={(d) => <span title={d.raw || ''}>{d.reason || (d.status ? `Investment ${d.status}` : '—')}</span>} />
                </DataTable>
            )}
            {decisions.length > 0 && note && <small className="text-color-secondary">{note}</small>}
            {allocations_unavailable && <small className="text-color-secondary">Allocation names unavailable - LoanSweeper is not connected.</small>}
            {source_error && <div className="ex-inline-error">Bid decisions could not be loaded: {source_error}</div>}
        </div>
    );
};

const RECENT_FIRST = 10;
const RECENT_MAX = 50;

const LoanLookup = ({ platform, initialLoanId }) => {
    const [loanId, setLoanId] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [checking, setChecking] = useState(false);
    const [recent, setRecent] = useState(null);
    const [recentLimit, setRecentLimit] = useState(RECENT_FIRST);

    // The 10 newest loans load straight away (cached); "Show more" fetches up to 50.
    const loadRecent = (limit, force = false) => {
        setRecentLimit(limit);
        setRecent(null);
        cachedGet(`/activity/platforms/${encodeURIComponent(platform)}/loans`, { params: { limit }, force })
            .then((res) => setRecent(res.data.loans))
            .catch(() => setRecent([]));
    };

    useEffect(() => loadRecent(RECENT_FIRST), [platform]); // eslint-disable-line react-hooks/exhaustive-deps

    const check = (id, force = false) => {
        const q = String(id || loanId).trim();
        if (!q) return;
        setLoanId(q);
        setChecking(true);
        setError(null);
        setResult(null);
        cachedGet(`/activity/platforms/${encodeURIComponent(platform)}/loans/${encodeURIComponent(q)}`, { force })
            .then((res) => setResult(res.data))
            .catch((err) => setError(errMsg(err)))
            .finally(() => setChecking(false));
    };

    // Opened from the allocation view with a loan to explain.
    useEffect(() => {
        if (initialLoanId) check(initialLoanId);
    }, [initialLoanId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <form className="ex-toolbar" onSubmit={(e) => { e.preventDefault(); check(); }}>
                <div className="ex-toolbar-group">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText value={loanId} onChange={(e) => setLoanId(e.target.value)} placeholder="Loan ID from the Originator" style={{ minWidth: '18rem' }} />
                    </span>
                    <Button type="submit" label="Why was / wasn't it bid?" icon="pi pi-question-circle" loading={checking} disabled={!loanId.trim()} />
                </div>
            </form>

            {error && <div className="ex-inline-error">{error}</div>}
            {checking && <LoadingState label="Checking this loan…" size="sm" />}
            {result && <LoanResult result={result} />}
            {result && (
                <Button label="Check again (fresh)" icon="pi pi-refresh" className="p-button-sm p-button-text"
                    onClick={() => check(result.loan_id, true)} disabled={checking} />
            )}

            <div className="act-section-bar">
                <span className="act-subtitle">Latest loans offered</span>
                <Button icon="pi pi-refresh" className="p-button-text p-button-sm" tooltip="Refresh" onClick={() => loadRecent(recentLimit, true)} />
            </div>
            {!recent && <LoadingState label="Loading loans…" size="sm" />}
            {recent && !recent.length && <EmptyState compact icon="pi-inbox" title="No recent loans in the opportunities feed"
                message="Some bidders (e.g. Anodu) fetch loans directly from the Originator - you can still look up a loan ID above." />}
            {recent && recent.length > 0 && (
                <DataTable value={recent} dataKey="loan_id" size="small" responsiveLayout="scroll" paginator rows={10}
                    selectionMode="single" onRowSelect={(e) => check(e.data.loan_id)} rowClassName={() => 'act-clickable'}>
                    <Column field="loan_id" header="Loan ID" />
                    <Column header="Received" body={(l) => <span title={fmtDateTime(l.received_at)}>{fmtAgo(l.received_at)}</span>} />
                    <Column header="Amount" body={(l) => `${fmtNum(l.amount)} ${l.currency || ''}`} />
                    <Column header="Rate" body={(l) => fmtPct(l.rate)} />
                    <Column field="term" header="Term" />
                    <Column header="Grade" body={(l) => l.grade || '—'} />
                    <Column header="" body={() => <span className="text-color-secondary">Check <i className="pi pi-angle-right" /></span>} />
                </DataTable>
            )}
            {recent && recent.length >= recentLimit && recentLimit < RECENT_MAX && (
                <Button label="Show more" icon="pi pi-angle-down" className="p-button-text p-button-sm" onClick={() => loadRecent(RECENT_MAX)} />
            )}
        </div>
    );
};

export default LoanLookup;
